import {
  ApolloClient,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client';
import { _getLogger } from '@doxyme/analytics';
import type {
  Query,
  Mutation,
  ClientExtensionMutationsActivateArgs,
  ClientExtensionMutationsDeactivateArgs,
  ApplicationQueriesFindAllArgs,
  ApplicationQueriesFindAllForPatientArgs,
  FindAllApplicationsInput,
  FindAllApplicationsForPatientInput,
  Application,
  ApplicationForPatient,
  GenerateAppTokenInput,
  AppToken,
  AppTokenMutationsGenerateAppTokenArgs,
} from '@doxyme/extensions-graphql-types';
import {
  ACTIVATE,
  DEACTIVATE,
  GET_APPLICATIONS,
  GET_APPLICATIONS_FOR_PATIENT,
  GENERATE_APP_TOKEN,
} from './schema.helper';

const logger = _getLogger('registry', {
  component: 'extensions',
  lib: 'bridge',
});

export class ExtensionRegistry {
  protected apiUrl: string | undefined;

  // it is initialized in the constructor
  protected api!: ApolloClient<NormalizedCacheObject>;

  private headers: Record<string, string> | undefined;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
    this.initApiClient();
  }

  initApiClient(authorizationHeader?: string): void {
    this.headers = authorizationHeader
      ? { Authorization: authorizationHeader }
      : undefined;

    this.api = new ApolloClient({
      uri: this.apiUrl,
      // TODO: [Tech spike] Investigate the cache feature: https://doxyme.atlassian.net/browse/CS-14922
      cache: new InMemoryCache(),
      headers: this.headers,
    });
  }

  async getApplications(
    input: FindAllApplicationsInput,
  ): Promise<Application[]> {
    try {
      this.checkIfHeadersAreSet();

      const result = await this.api.query<Query, ApplicationQueriesFindAllArgs>(
        {
          query: GET_APPLICATIONS,
          variables: {
            input,
          },
          fetchPolicy: 'network-only',
        },
      );
      return result.data.ExtensionsRegistry.application.findAll;
    } catch (error) {
      logger.error('Request to graphql getApplications endpoint failed', {
        error,
      });
      throw error;
    }
  }

  async getApplicationsForPatient(
    input: FindAllApplicationsForPatientInput,
  ): Promise<ApplicationForPatient[]> {
    try {
      const result = await this.api.query<
        Query,
        ApplicationQueriesFindAllForPatientArgs
      >({
        query: GET_APPLICATIONS_FOR_PATIENT,
        variables: {
          input,
        },
        // force network, to work correctly after provider's update message
        // function param can be added if different behavior is needed
        fetchPolicy: 'network-only',
      });
      return result.data.ExtensionsRegistry.application.findAllForPatient;
    } catch (error) {
      logger.error(
        'Request to graphql getApplicationsForPatient endpoint failed',
        { error },
      );
      throw error;
    }
  }

  async activate(extensionId: string): Promise<boolean | undefined> {
    try {
      this.checkIfHeadersAreSet();

      const result = await this.api.mutate<
        Mutation,
        ClientExtensionMutationsActivateArgs
      >({
        mutation: ACTIVATE,
        variables: {
          input: {
            extensionId,
          },
        },
      });

      return result.data?.ExtensionsRegistry.clientExtension.activate;
    } catch (error) {
      logger.error('Request to graphql activateBy endpoint failed', { error });
      throw error;
    }
  }

  async deactivate(extensionId: string): Promise<boolean | undefined> {
    try {
      this.checkIfHeadersAreSet();

      const result = await this.api.mutate<
        Mutation,
        ClientExtensionMutationsDeactivateArgs
      >({
        mutation: DEACTIVATE,
        variables: {
          input: {
            extensionId,
          },
        },
      });

      return result.data?.ExtensionsRegistry.clientExtension.deactivate;
    } catch (error) {
      logger.error('Request to graphql deactivateBy endpoint failed', {
        error,
      });
      throw error;
    }
  }

  async generateAppToken(input: GenerateAppTokenInput): Promise<AppToken> {
    try {
      this.checkIfHeadersAreSet();

      const result = await this.api.mutate<
        Mutation,
        AppTokenMutationsGenerateAppTokenArgs
      >({
        mutation: GENERATE_APP_TOKEN,
        variables: {
          input,
        },
      });

      if (!result.data?.ExtensionsRegistry.appToken.generateAppToken) {
        throw new Error('App token generation failed');
      }

      return result.data.ExtensionsRegistry.appToken.generateAppToken;
    } catch (error) {
      logger.error('[generateAppToken] Request to graphql endpoint failed', {
        error,
        input,
      });
      throw error;
    }
  }

  private checkIfHeadersAreSet(): void {
    if (this.headers === undefined) {
      throw new Error(
        `The required property headers has not been set to registry`,
      );
    }
  }
}
